import { el, render } from "../dom.ts";
import { makeMovable, type Movable } from "../drag.ts";
import { origamiSvg } from "../origami.ts";
import { mountFold, type Fold } from "./fold.ts";
import { answerTurn } from "./answer.ts";
import { onMenuVisible } from "../afterIntro.ts";
import {
  activeProvider, aiConfig, hasCredential, probeServerKeys,
  providerLabel, serverKeys,
} from "../../core/ai/config.ts";
import { ask, conversation, isAsking, resetConversation, stopAsking } from "../../core/ai/conversation.ts";
import { openAiSettings } from "../dock/tray.ts";
import { mountMic, type Mic } from "../voice/mic.ts";
import { intent } from "../../core/state/intent.ts";
import { selection } from "../../core/state/selection.ts";

/**
 * El asistente: un papel que se coloca donde se quiera y, al pulsarlo, abre una
 * ventana de conversación que también se coloca donde se quiera.
 *
 * La forma es la del botón de enviar de la pantalla de entrada —el mismo avión
 * de cinco caras, sin nada alrededor— porque es el gesto que la persona ya
 * conoce: el papel es lo que lleva y trae lo que se escribe. `origami.ts` es el
 * único sitio donde vive esa figura, así que evoluciona en los dos lados a la
 * vez.
 *
 * Ni el asa ni la ventana están ancladas: `drag.ts` guarda su posición y la
 * respeta al recargar. Es la instrucción explícita —«así lo podré mover o
 * posicionar donde desee»— y también lo que hace que el asistente pueda mirar
 * el documento sin taparlo.
 *
 * Los ajustes —proveedor, credencial y modelo— **no** están aquí: están en la
 * bandeja de la franja, que se ve incluso con la franja plegada. Estuvieron en las
 * dos partes y sobraba una. El pie ya no repite el modelo, que los ajustes lo
 * muestran al elegirlo; sólo habla cuando falta la credencial y, entonces,
 * lleva hasta allí (`openAiSettings`).
 *
 * Lo que ve, lo ve `conversation.ts`; aquí sólo se dice.
 */

/** Preguntas de arranque. Son del método, no de cortesía. */
const SEEDS: readonly string[] = [
  "¿Qué le falta a mi intención para poder indagarla?",
  "¿Cuál es la naturaleza de la situación que describo?",
  "¿Qué debería indagar primero, y con qué?",
];

/** Tope de altura del campo: a partir de ahí, se desplaza dentro. */
const FIELD_MAX = 150;
/** Margen para decidir si el registro estaba abajo antes de crecer. */
const NEAR_BOTTOM = 80;

let handle: HTMLButtonElement | null = null;
let panel: HTMLElement | null = null;
let log: HTMLElement | null = null;
let field: HTMLTextAreaElement | null = null;
let sendBtn: HTMLButtonElement | null = null;
let ctxLine: HTMLElement | null = null;
let footLine: HTMLElement | null = null;
let mic: Mic | null = null;
let panelMove: Movable | null = null;
let handleMove: Movable | null = null;
let fold: Fold | null = null;

/* --- lo que se dice ------------------------------------------------------- */

function turnLine(role: "user" | "assistant" | "system", text: string): HTMLElement {
  const kind = role === "user" ? " ai-turn--mine" : "";
  return el("div", { class: `ai-turn${kind}`, text });
}function emptyState(): HTMLElement {
  return el("div", { class: "ai-empty" }, [
    el("p", { class: "ai-empty__lead", text:
      "Pregunta lo que quieras del proyecto. No lo va a escribir por ti: " +
      "te va a devolver lo que falta por decidir." }),
    el("div", { class: "ai-seeds" }, SEEDS.map((seed) => el("button", {
      class: "ai-seed",
      text: seed,
      attrs: { type: "button" },
      on: { click: () => { void submit(seed); } },
    }))),
  ]);
}

function paintLog(): void {
  if (!log) return;
  const turns = conversation();
  if (turns.length === 0) {
    render(log, emptyState());
    return;
  }
  // Las respuestas se componen como el modo lectura y traen sus acciones; lo
  // propio de la persona, tal cual lo escribió.
  render(log, ...turns.map((turn) =>
    turn.role === "assistant" ? answerTurn(turn.content) : turnLine(turn.role, turn.content)));
  log.scrollTop = log.scrollHeight;
}

/** Qué está viendo el asistente, dicho en la cabecera para que no sea magia. */
function paintContext(): void {
  if (!ctxLine) return;
  const seen: string[] = [];
  if (intent.get()?.text) seen.push("tu intención");
  const page = selection.get();
  if (page) seen.push(`«${page.name}»`);
  ctxLine.textContent = seen.length > 0
    ? `Ve ${seen.join(" y ")}`
    : "Todavía no ve nada: abre una página";
}

/**
 * El pie habla solo cuando falta algo: la credencial. Con todo en orden no añade
 * nada a lo que ya dicen los ajustes —el modelo se ve al elegirlo—, y su espacio
 * se libera para la conversación.
 */
function paintFoot(): void {
  if (!footLine) return;
  const provider = activeProvider();
  // Por nombre y no por la tabla: los proveedores propios no están en ella.
  const label = providerLabel(provider);

  if (hasCredential(provider)) {
    footLine.hidden = true;
    render(footLine);
    return;
  }

  footLine.hidden = false;
  render(footLine,
    `${label}: falta la credencial. `,
    el("button", {
      class: "lnkbtn",
      text: "Configúrala aquí",
      attrs: { type: "button" },
      on: { click: () => { openAiSettings(); } },
    }),
  );
}

/* --- preguntar ------------------------------------------------------------ */

function busy(on: boolean): void {
  if (!sendBtn) return;
  sendBtn.classList.toggle("is-busy", on);
  const label = on ? "Detener la respuesta" : "Preguntar";
  sendBtn.setAttribute("aria-label", label);
  sendBtn.title = label;
}

/** El campo crece con lo escrito hasta un tope; luego se desplaza dentro. */
function grow(): void {
  if (!field) return;
  field.style.height = "auto";
  field.style.height = `${Math.min(field.scrollHeight, FIELD_MAX)}px`;
}

async function submit(text: string): Promise<void> {
  const box = log;
  if (!box) return;

  const question = text.trim();
  if (!question || isAsking()) return;

  if (field) {
    field.value = "";
    grow();
  }

  // El estado vacío desaparece con la primera pregunta, no antes.
  if (conversation().length === 0) render(box);
  box.append(turnLine("user", question));

  // La respuesta crece ya compuesta: mientras llega va tomando forma de
  // documento —títulos, listas— igual que tendrá al quedar pintada.
  let live: HTMLElement | null = el("div", { class: "ai-turn ai-turn--wait", text: "…" });
  const answer = live;
  box.append(answer);
  box.scrollTop = box.scrollHeight;
  busy(true);

  let full = "";
  let composed = "";
  try {
    await ask(question, {
      onDelta: (chunk) => {
        // Se mira antes de crecer: si la persona había subido a leer algo, no
        // se le arrastra la vista hacia abajo con cada trozo.
        const near = box.scrollHeight - box.scrollTop - box.clientHeight < NEAR_BOTTOM;
        full += chunk;
        // Se compone en cada bloque cerrado: rearmar todo el DOM por cada
        // trozo de diez caracteres sería tirar el nodo a cada paso. Con una
        // respuesta larga, cada doble salto de línea ya es un buen momento.
        const tail = full.slice(-64);
        if (composed === full || (!/\n\n/.test(tail) && full.length - composed.length < 96)) {
          if (near) box.scrollTop = box.scrollHeight;
          return;
        }
        composed = full;
        const fresh = answerTurn(full);
        answer.replaceWith(fresh);
        live = fresh;
        if (near) box.scrollTop = box.scrollHeight;
      },
    });

    // Lo último, compuesto y con acciones, una vez terminó de llegar.
    if (live) live.replaceWith(answerTurn(full));
    live = null;

    if (!full.trim() && !box.querySelector(".ai-turn--answer")) {
      box.append(el("div", { class: "ai-turn ai-turn--bad", text:
        "El proveedor no devolvió texto. Prueba con otro modelo." }));
    }
  } catch (error) {
    const stopped = error instanceof DOMException && error.name === "AbortError";
    if (live) {
      live.replaceWith(stopped && full.trim()
        ? answerTurn(full)
        : el("div", { class: "ai-turn ai-turn--bad", text: stopped
          ? (full.trim() ? full : "Detenido.")
          : (error instanceof Error ? error.message : "No se pudo preguntar.") }));
      live = null;
    }
  } finally {
    busy(false);
  }
}

/* --- construcción --------------------------------------------------------- */

let panelGrip: HTMLElement | null = null;

function buildHandle(): HTMLButtonElement {
  return el("button", {
    class: "ai-handle",
    attrs: {
      type: "button", hidden: true,
      "aria-label": "Asistente", "aria-expanded": "false", "aria-controls": "ai-pop",
      title: "Asistente · arrástralo donde quieras",
    },
    on: {
      click: () => {
        // Si se acaba de arrastrar, esto es el clic del arrastre y no una orden.
        if (handleMove?.dragged()) return;
        toggle();
      },
    },
  }, [origamiSvg("ai-handle__ori")]);
}

function buildPanel(): HTMLElement {
  ctxLine = el("span", { class: "ai-pop__ctx" });

  const grip = el("header", {
    class: "ai-pop__grip",
    attrs: { tabindex: "0", "aria-label": "Mover el asistente con las flechas del teclado" },
  }, [
    el("span", { class: "ai-pop__title", text: "Asistente" }),
    ctxLine,
    el("button", {
      class: "ai-pop__act",
      text: "Nueva",
      attrs: { type: "button", title: "Empezar otra conversación" },
      on: { click: () => {
        resetConversation(); paintLog(); busy(false); field?.focus();
      } },
    }),
    el("button", {
      class: "ai-pop__act ai-pop__x",
      text: "×",
      attrs: { type: "button", "aria-label": "Cerrar el asistente" },
      on: { click: () => { close(); handle?.focus(); } },
    }),
  ]);
  panelGrip = grip;

  log = el("div", {
    class: "ai-pop__log",
    attrs: { role: "log", "aria-live": "polite", "aria-label": "Conversación" },
  });

  const area = el("textarea", {
    class: "ai-pop__field",
    attrs: { rows: 1, placeholder: "Pregúntale, o cuéntale en qué estás…", "aria-label": "Tu pregunta" },
    on: {
      input: grow,
      keydown: (event) => {
        // Enter pregunta; Mayús+Enter hace párrafo. Es lo que ya hace la entrada.
        if (event.key !== "Enter" || event.shiftKey) return;
        event.preventDefault();
        void submit(area.value);
      },
    },
  });
  field = area;

  sendBtn = el("button", {
    class: "ai-pop__send",
    attrs: { type: "submit", "aria-label": "Preguntar", title: "Preguntar" },
  }, [origamiSvg("ai-pop__ori")]);

  /* Dictar la pregunta. Va antes del avión porque el orden es el del gesto:
     se escribe o se dicta, y después se manda. */
  mic = mountMic({ field: area, className: "mic ai-pop__mic" });

  const form = el("form", {
    class: "ai-pop__ask",
    on: {
      submit: (event) => {
        event.preventDefault();
        if (isAsking()) { stopAsking(); return; }
        void submit(area.value);
      },
    },
  }, mic ? [area, mic.button, sendBtn] : [area, sendBtn]);

  footLine = el("p", { class: "ai-pop__foot" });

  const box = el("section", {
    class: "ai-pop",
    attrs: { id: "ai-pop", hidden: true, "aria-label": "Asistente 3i" },
    on: {
      keydown: (event) => {
        if (event.key !== "Escape") return;
        event.stopPropagation();
        close();
        handle?.focus();
      },
    },
  }, [grip, log, form, footLine]);

  return box;
}

/* --- abrir, cerrar, aparecer ---------------------------------------------- */

function ensure(): void {
  if (panel) return;

  handle = buildHandle();
  panel = buildPanel();
  document.body.append(handle, panel);

  handleMove = makeMovable(handle, {
    name: "assistant.handle",
    // Abajo a la derecha, por encima del asa de la franja de trabajo.
    initial: (viewport, size) => ({ x: viewport.x - size.x - 22, y: viewport.y - size.y - 64 }),
  });
  panelMove = makeMovable(panel, {
    name: "assistant.panel",
    grip: panelGrip ?? panel,
    initial: (viewport, size) => ({
      x: viewport.x - size.x - 22,
      y: Math.max(16, viewport.y - size.y - 110),
    }),
  });

  // El pliegue va después de colocarlos: se mide el rectángulo de la ventana y
  // el del asa cada vez que se abre, así que aquí sólo hace falta que los dos
  // nodos existan y estén en la página.
  fold = mountFold(panel, handle);

  paintLog();
  paintContext();
  paintFoot();

  // El pie dice si falta la credencial, y la del servidor sólo la conoce
  // `/api/health`. Se pregunta al montar y no al abrir los ajustes: sin esto la
  // ventana anunciaría que falta una clave que sí está puesta.
  void probeServerKeys();

  intent.subscribe(paintContext);
  selection.subscribe(paintContext);
  aiConfig.subscribe(paintFoot);
  serverKeys.subscribe(paintFoot);
}

/**
 * El gesto del asa: soltar el papel al abrir, recogerlo al cerrar. La clase se
 * quita al acabar para devolver el mando a `is-on`, que es quien deja el papel
 * inclinado mientras la ventana está abierta.
 */
function pleat(name: "is-releasing" | "is-catching"): void {
  if (!handle) return;
  if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const node = handle;
  node.classList.remove("is-releasing", "is-catching");
  // Sin leer una medida por medio, quitar y poner la misma clase en el mismo
  // fotograma no reinicia la animación: el navegador no ve el cambio.
  void node.offsetWidth;
  node.classList.add(name);
  node.addEventListener("animationend", () => { node.classList.remove(name); }, { once: true });
}

/**
 * Abrir. El estado va primero y la animación después: lo que anuncia un lector
 * de pantalla no espera las seis décimas del pliegue.
 */
function open(): void {
  ensure();
  if (!handle || !panel) return;

  const wasShut = panel.hidden;

  handle.hidden = false;
  handleMove?.place();
  panel.hidden = false;
  // Se coloca ya visible: oculto no tiene medidas y no se podría encajar. El
  // pliegue lo tapa con opacidad, que sí las tiene.
  panelMove?.place();
  handle.setAttribute("aria-expanded", "true");
  handle.classList.add("is-on");
  field?.focus();

  // Sólo si estaba cerrada: `askAssistant` abre también con la ventana delante,
  // y volver a plegar lo que ya está desplegado sería un parpadeo.
  if (!wasShut) return;
  pleat("is-releasing");
  void fold?.open();
}

function close(): void {
  if (!panel || !handle) return;
  // Cerrar la ventana apaga el micrófono: seguir escuchando lo que se diga
  // delante de una ventana cerrada no lo pidió nadie.
  mic?.stop();
  // Antes de esconderla: el pliego necesita el rectángulo que ocupa para poder
  // recogerlo, y escondida no tiene ninguno.
  if (!panel.hidden) {
    void fold?.close();
    pleat("is-catching");
  }
  panel.hidden = true;
  handle.setAttribute("aria-expanded", "false");
  handle.classList.remove("is-on");
}

function toggle(): void {
  if (panel && !panel.hidden) close();
  else open();
}

/**
 * Llevar una pregunta al asistente desde otro sitio de la plataforma.
 *
 * El editor tiene tres acciones sobre lo que se selecciona —cuestionar, explicar,
 * precisar— y una revisión del documento entero, y ninguna de las cuatro abre su
 * propio panel de respuestas: van a esta ventana, que es donde la persona ya sabe
 * que le contestan y que puede mover para no taparse el texto. Es el principio de
 * no redundancia aplicado a nuestra propia interfaz: si hay dos sitios donde el
 * modelo contesta, sobra uno.
 *
 * Si está contestando otra cosa, la pregunta se queda escrita en el campo en vez
 * de tirarse o de cortar la respuesta que se está leyendo.
 */
export function askAssistant(question: string): void {
  const text = question.trim();
  if (!text) return;

  open();

  if (isAsking()) {
    if (field) {
      field.value = text;
      grow();
      field.focus();
    }
    return;
  }

  void submit(text);
}

/**
 * Monta el asa y la deja aparecer cuando termine la intro, con la misma cortesía
 * que la franja de trabajo. La ventana no se abre sola: aparece el papel.
 */
export function mountAssistant(): void {
  ensure();
  onMenuVisible(() => {
    if (!handle) return;
    handle.hidden = false;
    handleMove?.place();
    handle.classList.add("is-in");
    handle.addEventListener("animationend", () => { handle?.classList.remove("is-in"); }, { once: true });
  });
}
